import React from 'react';
import {
  Icon,
  IconButton,
  Box,
  Button,
  HStack,
  Stack,
  useMediaQuery,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useMobileBreakpoints from '@common/src/hooks/useMobileBreakpoints';
import { bph } from '@common/src/utils/styledComponentsUtils';

interface ActionButtonsProps {
  onBack?: () => void;
  onContinue: () => void;
  disabledContinue?: boolean;
  isLoading?: boolean;
  isVertical?: boolean;
  continueText?: string;
  backText?: string;
  customChildren?: React.ReactNode;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onBack,
  onContinue,
  disabledContinue = false,
  isLoading = false,
  isVertical = false,
  continueText,
  backText,
  customChildren,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const { isLaptop } = useMobileBreakpoints();

  const [verticalPxSufficient] = useMediaQuery(`(min-height: ${bph.svga}px)`, {
    ssr: false,
  });

  const scrollFade = {
    content: '""',
    position: 'absolute',
    zIndex: '1',
    bottom: '99%',
    left: '0',
    pointerEvents: 'none',
    w: '100%',
    h: '10',
    backgroundImage:
      'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(253, 252, 255, 1) 100%)',
  };

  return (
    <Box
      flexShrink={0}
      w="100%"
      position={verticalPxSufficient ? 'sticky' : 'static'}
      bottom="0"
      right="0"
      zIndex="1"
      p={{ base: '4', laptop: '6' }}
      pb={{ laptop: '8' }}
      borderTop={{ base: '1px', laptop: 'none' }}
      borderColor="gray.50"
      bg={{ base: 'white', laptop: '#fdfcff' }}
      _before={{ laptop: scrollFade }}
      {...otherProps}
    >
      <Stack
        direction={isVertical ? 'column-reverse' : 'row'}
        spacing={{ base: '3', tablet: '6' }}
        justifyContent="center"
        {...(isVertical && { alignItems: 'center' })}
      >
        {onBack && (
          <>
            {!isLaptop && (
              <IconButton
                onClick={onBack}
                variant="secondary"
                borderRadius="full"
                aria-label="Back"
                icon={<Icon name="GeneralActionsArrowLeft" />}
                minH="12"
                minW="12"
              />
            )}
            <Button
              display={{ base: 'none', laptop: 'inherit' }}
              leftIcon={<Icon name="GeneralActionsArrowLeft" />}
              minH="12"
              minW="64"
              onClick={onBack}
              borderRadius="full"
              variant="grayedGhost"
            >
              {backText || t('Go back')}
            </Button>
          </>
        )}

        <Button
          data-test-id="ProdFac_LargeButton"
          minH="12"
          minW={{ base: '48', laptop: '64' }}
          onClick={onContinue}
          borderRadius="full"
          w={{ base: '100%', laptop: 'auto' }}
          isDisabled={disabledContinue || isLoading}
          isLoading={isLoading}
        >
          {continueText || t('Continue')}
        </Button>

        {customChildren && (
          <HStack flexGrow={{ base: 1, laptop: 0 }}>{customChildren}</HStack>
        )}
      </Stack>
    </Box>
  );
};

export default ActionButtons;
